import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Modal, Select, Stack, Textarea, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';

import { createOrganization, deleteOrganization, listOrganizations } from '../../api/resources';
import { DataPanel } from '../../components/DataPanel';
import { DetailDrawer } from '../../components/detail/DetailDrawer';
import { OrganizationDetailPanel } from '../../components/detail/OrganizationDetailPanel';
import { EntityRef } from '../../components/EntityRef';
import { PageHeader } from '../../components/PageHeader';
import { PaginatedTable } from '../../components/PaginatedTable';
import { RemoveEntityAction } from '../../components/RemoveEntityAction';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { DEFAULT_PAGE_SIZE, useListQuery } from '../../hooks/useListQuery';
import type { Organization } from '../../types/api';
import { ORGANIZATION_TYPE_VALUES } from '../../types/domain';

export function OrganizationsPage() {
    const { t } = useTranslation();
    const [page, setPage] = useState(1);
    const [detail, setDetail] = useState<Organization | null>(null);
    const [nameFilter, setNameFilter] = useState('');
    const debouncedName = useDebouncedValue(nameFilter);
    const [createOpen, setCreateOpen] = useState(false);
    const queryClient = useQueryClient();

    const { data, isLoading } = useListQuery('organizations', listOrganizations, {
        page: debouncedName ? 1 : page,
        limit: debouncedName ? 500 : DEFAULT_PAGE_SIZE,
    });

    const filteredRows = useMemo(() => {
        const rows = data?.data ?? [];
        if (!debouncedName.trim()) {
            return rows;
        }
        const q = debouncedName.trim().toLowerCase();
        return rows.filter((o) => o.name.toLowerCase().includes(q));
    }, [data?.data, debouncedName]);

    const form = useForm({
        initialValues: { name: '', type: '', country: '', settingsJson: '{}' },
    });

    const createMutation = useMutation({
        mutationFn: () => {
            let settings: Record<string, unknown> = {};
            try {
                settings = JSON.parse(form.values.settingsJson) as Record<string, unknown>;
            } catch {
                throw new Error(t('features.organizations.invalidSettingsJson'));
            }
            return createOrganization({
                name: form.values.name,
                type: form.values.type || undefined,
                country: form.values.country || undefined,
                settings,
            });
        },
        onSuccess: () => {
            notifications.show({ color: 'green', message: t('features.organizations.created') });
            setCreateOpen(false);
            form.reset();
            void queryClient.invalidateQueries({ queryKey: ['organizations'] });
        },
        onError: (e: Error) => notifications.show({ color: 'red', message: e.message }),
    });

    return (
        <Stack gap="lg">
            <PageHeader
                title={t('features.organizations.title')}
                description={t('features.organizations.description')}
                actions={
                    <Button onClick={() => setCreateOpen(true)}>
                        {t('features.organizations.createAction')}
                    </Button>
                }
            />
            <DataPanel>
                <TextInput
                    label={t('features.organizations.filterName')}
                    placeholder={t('common.placeholders.orgNameFilter')}
                    value={nameFilter}
                    onChange={(e) => setNameFilter(e.currentTarget.value)}
                />
            </DataPanel>
            <PaginatedTable
                rowKey={(r) => r.id}
                columns={[
                    {
                        key: 'name',
                        header: t('fields.organization'),
                        render: (r) => (
                            <EntityRef
                                title={r.name}
                                id={r.id}
                                secondary={[r.type, r.country].filter(Boolean).join(' · ') || undefined}
                            />
                        ),
                    },
                    {
                        key: 'type',
                        header: t('fields.type'),
                        render: (r) =>
                            r.type
                                ? t(`organizationTypes.${r.type}`, { defaultValue: r.type })
                                : t('common.empty'),
                    },
                    {
                        key: 'country',
                        header: t('fields.country'),
                        render: (r) => r.country ?? t('common.empty'),
                    },
                ]}
                rows={filteredRows}
                total={debouncedName ? filteredRows.length : (data?.total ?? 0)}
                page={debouncedName ? 1 : page}
                pageSize={debouncedName ? filteredRows.length || DEFAULT_PAGE_SIZE : DEFAULT_PAGE_SIZE}
                onPageChange={setPage}
                isLoading={isLoading}
                onRowClick={setDetail}
            />
            <DetailDrawer
                opened={Boolean(detail)}
                onClose={() => setDetail(null)}
                title={detail?.name ?? t('features.organizations.drawerFallbackTitle')}
                subtitle={detail?.id}
                footer={
                    detail ? (
                        <RemoveEntityAction
                            entityId={detail.id}
                            entityName={detail.name}
                            deleteFn={deleteOrganization}
                            queryKeys={['organizations']}
                            successMessageKey="features.organizations.removed"
                            onRemoved={() => setDetail(null)}
                        />
                    ) : undefined
                }
            >
                {detail ? <OrganizationDetailPanel organization={detail} /> : null}
            </DetailDrawer>
            <Modal
                opened={createOpen}
                onClose={() => setCreateOpen(false)}
                title={t('features.organizations.createModalTitle')}
            >
                <Stack>
                    <TextInput label={t('fields.name')} required {...form.getInputProps('name')} />
                    <Select
                        label={t('fields.type')}
                        clearable
                        data={ORGANIZATION_TYPE_VALUES.map((orgType) => ({
                            value: orgType,
                            label: t(`organizationTypes.${orgType}`),
                        }))}
                        value={form.values.type || null}
                        onChange={(v) => form.setFieldValue('type', v ?? '')}
                    />
                    <TextInput label={t('fields.country')} {...form.getInputProps('country')} />
                    <Textarea
                        label={t('fields.settingsJson')}
                        minRows={4}
                        {...form.getInputProps('settingsJson')}
                    />
                    <Button loading={createMutation.isPending} onClick={() => createMutation.mutate()}>
                        {t('common.actions.create')}
                    </Button>
                </Stack>
            </Modal>
        </Stack>
    );
}
